import { Badge } from "@/components/ui/badge";
import { ClassificationBadge } from "@/components/ui/classification-badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetTargetareFirma } from "@/hooks/api/query/useGetTargetareFirma";
import type {
  TargetareBilant,
  TargetareCodCaen,
  TargetareFisaFirma,
  TargetareLegaturaFirma,
  TargetareServiciuPotrivit,
  TargetareSucursalaUe,
  TargetareVersiuneCaen,
} from "@/types/targetare";
import {
  formatDataOra,
  formatDimensiune,
  formatNumar,
  formatRon,
} from "@/utils/targetareFormatting";
import { ContacteFirma } from "./ContacteFirma";
import { PersoaneFirma } from "./PersoaneFirma";
import { TierBadge } from "./TierBadge";

type FisaFirmaDialogProps = {
  cui: string | null;
  onClose: () => void;
};

function Sectiune({
  titlu,
  children,
}: {
  titlu: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-heading font-semibold uppercase tracking-wide text-muted-foreground">
        {titlu}
      </h3>
      {children}
    </section>
  );
}

function Camp({ eticheta, valoare }: { eticheta: string; valoare: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{eticheta}</dt>
      <dd className="text-sm">{valoare || "–"}</dd>
    </div>
  );
}

const FEL_LABELS: Record<TargetareServiciuPotrivit["fel"], string> = {
  principal: "CAEN principal",
  lista: "listă",
  autorizat: "CAEN autorizat",
};

function ListaServicii({
  servicii,
}: {
  servicii: TargetareServiciuPotrivit[];
}) {
  if (servicii.length === 0) {
    return <p className="text-sm text-muted-foreground">Nicio potrivire.</p>;
  }
  return (
    <ul className="space-y-1">
      {servicii.map((s) => (
        <li
          key={`${s.entity_id}-${s.fel}`}
          className="flex items-start gap-2 text-sm"
        >
          <Badge variant={s.selectat ? "default" : "outline"}>
            {typeof s.scor === "number" ? `${s.scor}` : "–"}
            {s.selectat ? " ✓" : ""}
          </Badge>
          <Badge variant={s.fel === "autorizat" ? "outline" : "secondary"}>
            {FEL_LABELS[s.fel]}
          </Badge>
          <span>
            {s.name}
            <span className="ml-1 font-mono text-xs text-muted-foreground">
              {s.entity_id}
            </span>
            {s.ambiguitate && (
              <span className="ml-1 text-xs text-muted-foreground">
                · ambiguitate {s.ambiguitate}
              </span>
            )}
            {(s.potrivire_caen || s.potrivire_lista) && (
              <span className="ml-1 text-xs text-muted-foreground">
                · scenariu {s.potrivire_caen || "fără CAEN"}
                {s.potrivire_lista ? ` + listă ${s.potrivire_lista}` : ""}
              </span>
            )}
            {s.coduri && s.coduri.length > 0 && (
              <span className="ml-1 font-mono text-xs text-muted-foreground">
                · {s.coduri.join(", ")}
              </span>
            )}
            {s.rezultat && (
              <span className="ml-1 text-xs text-muted-foreground">
                · regula {s.regula}: {s.rezultat}
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

const ETICHETE_SURSA_COD: Record<string, string> = {
  onrc: "ONRC",
  termene: "Termene (RECOM)",
  anaf: "ANAF",
};

function CoduriCaen({
  coduri,
  versiune,
  descrieri,
  istoric = false,
}: {
  coduri: TargetareCodCaen[];
  versiune: TargetareVersiuneCaen;
  descrieri: Record<string, string>;
  istoric?: boolean;
}) {
  const aleVersiunii = coduri.filter((c) => c.ver === versiune);
  if (aleVersiunii.length === 0) return null;
  return (
    <div className={istoric ? "opacity-60" : undefined}>
      <p className="text-xs text-muted-foreground">
        {versiune === 3
          ? "CAEN Rev. 3 (clasificarea în vigoare din 2025) — setul curent"
          : istoric
            ? "CAEN Rev. 2 — istoric, înlocuit de setul Rev. 3 (nu se folosește în clasificare)"
            : "CAEN Rev. 2 — setul curent (firma nu a fost încă recodificată în Rev. 3)"}
      </p>
      <ul className="mt-1 flex flex-wrap gap-1">
        {aleVersiunii.map((c) => (
          <li key={`${c.ver}-${c.cod}`}>
            <Badge
              variant={c.principal ? "secondary" : "outline"}
              className="font-mono"
              title={[
                descrieri[`${c.cod}/${c.ver}`] ?? "",
                `sursă: ${ETICHETE_SURSA_COD[c.sursa] ?? c.sursa}`,
              ]
                .filter(Boolean)
                .join(" · ")}
            >
              {c.cod}
              {c.principal ? " ★" : ""}
            </Badge>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SucursaleUe({ sucursale }: { sucursale: TargetareSucursalaUe[] }) {
  if (sucursale.length === 0) return null;
  return (
    <Sectiune titlu="Sucursale în alte state membre UE">
      <ul className="space-y-1 text-sm">
        {sucursale.map((s, i) => (
          <li key={`${s.euid ?? ""}-${i}`} className="flex flex-wrap gap-x-2">
            {s.tara && <Badge variant="outline">{s.tara}</Badge>}
            <span>{s.denumire ?? "–"}</span>
            {s.euid && (
              <span className="font-mono text-xs text-muted-foreground">
                {s.euid}
              </span>
            )}
            {s.tip_unitate && (
              <span className="text-xs text-muted-foreground">
                · {s.tip_unitate}
              </span>
            )}
            {s.cod_fiscal_strain && (
              <span className="font-mono text-xs text-muted-foreground">
                · {s.cod_fiscal_strain}
              </span>
            )}
          </li>
        ))}
      </ul>
    </Sectiune>
  );
}

const ETICHETE_TIP_LEGATURA: Record<string, string> = {
  legata: "întreprindere legată (> 50 %)",
  partenera: "întreprindere parteneră (25–50 %)",
  persoana_comuna: "persoană comună (semnal)",
  mama_straina: "firmă-mamă străină",
};

function LegaturiGrup({ legaturi }: { legaturi: TargetareLegaturaFirma[] }) {
  if (legaturi.length === 0) return null;
  return (
    <Sectiune titlu="Întreprinderi legate și partenere">
      <p className="mb-2 text-xs text-muted-foreground">
        Conform Recomandării 2003/361/CE, dimensiunea se calculează cu datele
        întreprinderilor legate (integral) și partenere (proporțional).
        Legăturile de mai jos sunt orientative și nu schimbă automat încadrarea.
      </p>
      <ul className="space-y-1 text-sm">
        {legaturi.map((l, i) => (
          <li
            key={`${l.cui_legat ?? l.identificator_extern ?? ""}-${l.sens ?? ""}-${i}`}
            className="flex flex-wrap items-baseline gap-x-2"
          >
            <Badge variant="outline">
              {l.sens === "amonte" ? "deține firma" : "deținută de firmă"}
            </Badge>
            <span>{l.nume ?? "–"}</span>
            {(l.cui_legat || l.identificator_extern) && (
              <span className="font-mono text-xs text-muted-foreground">
                {l.cui_legat ?? l.identificator_extern}
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {l.procent !== null ? `${l.procent} %` : "procent necunoscut"}
              {l.tip ? ` · ${ETICHETE_TIP_LEGATURA[l.tip] ?? l.tip}` : ""}
              {l.tara ? ` · ${l.tara}` : ""}
              {l.sursa ? ` · sursă ${l.sursa}` : ""}
            </span>
          </li>
        ))}
      </ul>
    </Sectiune>
  );
}

function TabelBilant({ bilanturi }: { bilanturi: TargetareBilant[] }) {
  if (bilanturi.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Fără situații financiare în snapshot.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted-foreground">
          <tr>
            <th className="py-1 pr-3">An</th>
            <th className="py-1 pr-3">CAEN declarat</th>
            <th className="py-1 pr-3 text-right">Cifra de afaceri</th>
            <th className="py-1 pr-3 text-right">Rezultat net</th>
            <th className="py-1 pr-3 text-right">Active totale</th>
            <th className="py-1 pr-3 text-right">Salariați</th>
          </tr>
        </thead>
        <tbody>
          {bilanturi.map((b) => (
            <tr key={b.an} className="border-t">
              <td className="py-1 pr-3 tabular-nums">{b.an}</td>
              <td className="py-1 pr-3 font-mono text-xs">
                {b.caen_declarat ?? "–"}
                {b.caen_ver ? ` (Rev. ${b.caen_ver})` : ""}
              </td>
              <td className="py-1 pr-3 text-right tabular-nums">
                {formatRon(b.cifra_afaceri)}
              </td>
              <td className="py-1 pr-3 text-right tabular-nums">
                {b.profit_net
                  ? formatRon(b.profit_net)
                  : b.pierdere_net
                    ? `-${formatRon(b.pierdere_net)}`
                    : "–"}
              </td>
              <td className="py-1 pr-3 text-right tabular-nums">
                {formatRon(
                  (b.active_imobilizate ?? 0) + (b.active_circulante ?? 0)
                )}
              </td>
              <td className="py-1 pr-3 text-right tabular-nums">
                {formatNumar(b.salariati)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContinutFisa({ fisa }: { fisa: TargetareFisaFirma }) {
  const { firma, clasificare } = fisa;
  const coduri = fisa.coduri ?? [];
  const verCurenta: TargetareVersiuneCaen | null =
    fisa.caen_curent?.ver_curenta ??
    (coduri.some((c) => c.ver === 3) ? 3 : coduri.length > 0 ? 2 : null);
  const caenPrincipal = coduri.find((c) => c.principal) ?? null;
  return (
    <div className="space-y-6">
      <Sectiune titlu="Identificare">
        <dl className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <Camp eticheta="CUI" valoare={firma.cui} />
          <Camp
            eticheta="Nr. înmatriculare"
            valoare={firma.cod_inmatriculare}
          />
          <Camp
            eticheta="Formă juridică"
            valoare={firma.forma_juridica ?? ""}
          />
          <Camp
            eticheta="Adresă"
            valoare={[firma.adresa, firma.localitate, firma.judet]
              .filter(Boolean)
              .join(", ")}
          />
          <Camp eticheta="Web" valoare={firma.web ?? ""} />
          <Camp
            eticheta="Înmatriculată la"
            valoare={firma.data_inmatriculare ?? ""}
          />
          <Camp eticheta="EUID" valoare={firma.euid ?? ""} />
          <Camp
            eticheta="Telefon (registrul ANAF)"
            valoare={firma.telefon_anaf ?? ""}
          />
          <Camp
            eticheta="TVA"
            valoare={
              firma.tva === null || firma.tva === undefined
                ? ""
                : firma.tva
                  ? "da"
                  : "nu"
            }
          />
          <Camp
            eticheta="Stare fiscală (ANAF)"
            valoare={[firma.stare_anaf, firma.data_stare_anaf]
              .filter(Boolean)
              .join(" · ")}
          />
          {firma.tara_firma_mama && (
            <Camp
              eticheta="Firmă-mamă în străinătate"
              valoare={firma.tara_firma_mama}
            />
          )}
          {!firma.activa && (
            <Camp
              eticheta="Inactivă din"
              valoare={firma.inactiva_din ?? "dată necunoscută"}
            />
          )}
        </dl>
        <div className="flex flex-wrap gap-1">
          {fisa.stari.map((s) => (
            <Badge
              key={s.cod}
              variant={s.cod === "1048" ? "secondary" : "outline"}
              className="font-normal"
              title={s.categorie ? `categorie: ${s.categorie}` : undefined}
            >
              {s.denumire ?? s.cod}
              {s.categorie && s.categorie !== "functiune" ? (
                <span className="ml-1 text-[10px] uppercase tracking-wide opacity-70">
                  {s.categorie}
                </span>
              ) : null}
            </Badge>
          ))}
        </div>
        {fisa.snapshot && (
          <p className="text-xs text-muted-foreground">
            Sursa datelor de identificare:{" "}
            {fisa.snapshot.eticheta ?? `snapshot ONRC ${fisa.snapshot.id}`}
            {fisa.snapshot.data_publicarii
              ? ` (publicat ${fisa.snapshot.data_publicarii})`
              : ""}
          </p>
        )}
      </Sectiune>

      <Sectiune titlu="Clasificare NIS2 (estimată)">
        {clasificare ? (
          <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div>
              <dt className="text-xs text-muted-foreground">Tier</dt>
              <dd>
                <TierBadge tier={clasificare.tier} />
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Încadrare</dt>
              <dd>
                <ClassificationBadge
                  classification={clasificare.clasificare_estimata}
                  size="sm"
                />
              </dd>
            </div>
            <Camp
              eticheta="Dimensiune (strictă / largă)"
              valoare={`${formatDimensiune(clasificare.dimensiune_stricta)} / ${formatDimensiune(clasificare.dimensiune_larga)}`}
            />
            <Camp
              eticheta="Scor potrivire"
              valoare={
                clasificare.scor === null ? "" : String(clasificare.scor)
              }
            />
            <Camp
              eticheta="Sectoare"
              valoare={clasificare.sectoare.join(", ")}
            />
            <Camp
              eticheta="Calculată la"
              valoare={formatDataOra(clasificare.calculat_la)}
            />
          </dl>
        ) : (
          <p className="text-sm text-muted-foreground">
            Firma nu are potriviri cu servicii NIS2.
          </p>
        )}
      </Sectiune>

      <Sectiune titlu="Servicii NIS2 potrivite">
        <ListaServicii servicii={fisa.servicii} />
      </Sectiune>

      <Sectiune titlu="Coduri CAEN autorizate (setul curent)">
        <div className="space-y-2">
          <p className="rounded-md border bg-muted/40 p-2 text-xs">
            <span className="font-medium">
              Setul curent folosit în clasificare:
            </span>{" "}
            {verCurenta
              ? `${coduri.filter((c) => c.ver === verCurenta).length} coduri Rev. ${verCurenta}`
              : "fără coduri autorizate"}
            {caenPrincipal && (
              <>
                {" · principal "}
                <span className="font-mono">{caenPrincipal.cod}</span>
                {` (Rev. ${caenPrincipal.ver}, ${ETICHETE_SURSA_COD[caenPrincipal.sursa] ?? caenPrincipal.sursa}`}
                {fisa.caen_curent?.interogat_la
                  ? `, interogat la ${formatDataOra(fisa.caen_curent.interogat_la)}`
                  : ""}
                {")"}
              </>
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            ONRC publică pentru fiecare firmă codurile autorizate în versiunea
            CAEN în care au fost înregistrate. Clasificarea folosește numai
            setul curent: Rev. 3 dacă firma a fost recodificată, altfel Rev. 2.
            Codul principal (★) nu are rol la încadrare (decizia D10) — este
            informație afișată și un semnal de calitate a datelor.
          </p>
          <CoduriCaen
            coduri={coduri}
            versiune={3}
            descrieri={fisa.caen_descrieri}
          />
          <CoduriCaen
            coduri={coduri}
            versiune={2}
            descrieri={fisa.caen_descrieri}
            istoric={verCurenta === 3}
          />
          {coduri.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Fără coduri autorizate în snapshot.
            </p>
          )}
        </div>
      </Sectiune>

      <Sectiune titlu="Situații financiare">
        <TabelBilant bilanturi={fisa.bilanturi} />
      </Sectiune>

      {fisa.liste.length > 0 && (
        <Sectiune titlu="Liste oficiale și neoficiale pe care apare firma">
          <p className="mb-2 text-xs text-muted-foreground">
            Registrele publicate de autorități (ANRE, ANCOM, BNR, ANRSC,
            Ministerul Sănătății etc.) și listele întocmite manual în care a
            fost găsită firma. Fiecare rând este o listă, cu serviciul NIS2
            atribuit listei și numărul de înregistrări (licențe, autorizații,
            vehicule) pe care firma le are în ea.
          </p>
          <ul className="space-y-2 text-sm">
            {fisa.liste.map((l, i) => (
              <li
                key={`${l.fisier ?? ""}-${l.service_id ?? ""}-${i}`}
                className="rounded-md border p-2"
              >
                <div className="flex flex-wrap items-baseline gap-x-2">
                  {l.autoritate && (
                    <span className="font-medium">{l.autoritate}</span>
                  )}
                  <span title={l.fisier ?? undefined}>
                    {(l.sursa_nume ?? l.fisier ?? l.nume ?? "").replace(
                      /\.csv$/i,
                      ""
                    )}
                  </span>
                  {l.tip && (
                    <Badge variant="outline" className="text-xs">
                      {l.tip === "OFICIALA" ? "oficială" : "neoficială"}
                    </Badge>
                  )}
                  {l.activ === false && (
                    <Badge variant="outline" className="text-xs">
                      listă dezactivată
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {l.service_id && (
                    <span>
                      serviciul{" "}
                      <span className="font-mono">{l.service_id}</span>
                      {l.serviciu ? ` ${l.serviciu}` : ""}
                    </span>
                  )}
                  {typeof l.randuri === "number" && (
                    <span>
                      {" "}
                      · {l.randuri}{" "}
                      {l.randuri === 1 ? "înregistrare" : "înregistrări"}
                      {l.tipuri ? `: ${l.tipuri}` : ""}
                    </span>
                  )}
                  {l.nume && l.nume !== firma.denumire && (
                    <span> · pe listă ca „{l.nume}”</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Sectiune>
      )}

      <SucursaleUe sucursale={fisa.sucursale ?? []} />

      <LegaturiGrup legaturi={fisa.legaturi ?? []} />

      <Sectiune titlu="Persoane cu rol">
        <PersoaneFirma cui={firma.cui} persoane={fisa.persoane} />
      </Sectiune>

      <Sectiune titlu="Contacte">
        <ContacteFirma
          cui={firma.cui}
          contacte={fisa.contacte}
          excluderi={fisa.excluderi}
        />
      </Sectiune>
    </div>
  );
}

export function FisaFirmaDialog({ cui, onClose }: FisaFirmaDialogProps) {
  const { data, isLoading, error } = useGetTargetareFirma(cui);

  return (
    <Dialog open={!!cui} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {data?.firma.denumire ?? "Fișa firmei"}
          </DialogTitle>
          <DialogDescription className="font-mono">CUI {cui}</DialogDescription>
        </DialogHeader>
        {isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
        )}
        {error && (
          <p className="text-sm text-destructive">
            Fișa nu a putut fi încărcată: {error.message}
          </p>
        )}
        {data && <ContinutFisa fisa={data} />}
      </DialogContent>
    </Dialog>
  );
}
